import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GLOBAL_HEADERS } from 'src/app/store/store.constants';
import { OrderCodeResponse } from './order-code.model';
import { TranslationService } from '@core/services/common/translation.service';
import { retryLoadOrderCode } from './order-code.actions';
import { ArkikAlertService } from '@shared/components/arkik-alert/arkik-alert.service';

@Injectable({ providedIn: 'root' })
export class OrderCodeApiService {
  private urlApiOrder = environment.APIM + 'comorder/order';

  constructor(
    private http: HttpClient,
    private ts: TranslationService,
    private readonly arkikAlertService: ArkikAlertService,
  ) {}

  /**
   * Retrieves a new order code from the API.
   *
   * Sends a GET request to the order code endpoint, including the current date as a query parameter.
   * Returns an observable that emits the response containing the new order code.
   *
   * @returns {Observable<OrderCodeResponse>} An observable emitting the order code response from the API.
   */
  getNewOrderCode(): Observable<OrderCodeResponse> {
    return this.http.get<OrderCodeResponse>(
      this.urlApiOrder + '/GetNewOrderCode?date=' + new Date().toString(),
      GLOBAL_HEADERS,
    );
  }

  /**
   * Displays an error alert related to the order code process.
   *
   * This method retrieves localized error and call-to-action messages,
   * then notifies the user with an error alert using the `messageAlertService`.
   * The alert includes an action to retry loading the order code.
   *
   * @remarks
   * The error and CTA messages are fetched using the translation service (`ts.pt`).
   * The retry action is provided by the `retryLoadOrderCode` function.
   */
  showOrderCodeErrorAlert(): void {
    const errorMessage = this.ts.pt('views.orders.order-taking-decouple.order-code.error');
    const CTAMessage = this.ts.pt('views.orders.order-taking-decouple.order-code.retry');
    this.arkikAlertService.show({
      message: errorMessage,
      duration: 5000,
      keepOpen: false,
      variant: 'error',
      ctaAction: retryLoadOrderCode(),
      ctaMessage: CTAMessage,
    });
  }
}
